import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Stack,
    IconButton,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    createTheme,
    ThemeProvider,
    CssBaseline,
    Drawer, // Import Drawer
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'; // For Google Map icon
import Chat from '../Pages/Chat';
import { useDrawer } from '../../contexts/DrawerContext';
import LabWorkRequest from './LabWorkRequest';
import ViewPrescription from './PrescriptionView';
import FollowUpAppointment from './FollowUpAppointment';

// --- Custom MUI Theme (can be the same as ConsultationSummary or shared) ---
// For demonstration, let's assume a shared theme or consistent styling.
// If you want this component to have a slightly different look, define its own theme here.
const theme = createTheme({
    palette: {
        primary: {
            main: '#28a745', // Green
        },
        background: {
            default: '#f0f2f5',
            paper: '#fff',
        },
        text: {
            primary: '#333',
            secondary: '#6c757d',
        },
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
        h5: {
            fontWeight: 600,
            fontSize: '1.5rem',
        },
        h6: {
            fontWeight: 600,
            fontSize: '1.1rem',
        },
        body1: {
            fontSize: '0.95rem',
        },
        body2: {
            fontSize: '0.85rem',
            color: '#6c757d',
        },
    },
    components: {
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px', // Slightly rounded like the screenshot
                        backgroundColor: '#fdfdfd', // Light background for inputs
                        '& fieldset': {
                            borderColor: '#e0e0e0', // Light border
                        },
                        '&:hover fieldset': {
                            borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                        },
                    },
                    '& .MuiInputLabel-outlined': { // Style for label
                        color: "lightgray", // Gray label
                    },
                    '& .Mui-focused .MuiInputLabel-outlined': {
                        color: "green" // Primary color when focused
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '12px',
                    fontWeight: 600,
                },
                containedPrimary: {
                    backgroundColor: '#28a745',
                    color: '#fff',
                    '&:hover': {
                        backgroundColor: '#218838',
                    },
                    boxShadow: 'none',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    backgroundColor: '#fdfdfd',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#bdbdbd',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                    },
                }
            }
        }
    },
});

function RightDrawer({ open, onClose }) {
    const [hospital, setHospital] = useState('Healing Stripes Hospital, Victoria Island, Lagos');
    const [address, setAddress] = useState('5236 Aboyade Cole Street, Victoria Island, Lagos');
    const [contactPerson, setContactPerson] = useState('Dr. Williams Humphrey');
    const [phoneNumber, setPhoneNumber] = useState(''); // Not filled in screenshot
    const [severity, setSeverity] = useState('High (Emergency Admission)');
    const [additionalNote, setAdditionalNote] = useState('');


     const { toggleAdmissionDetailsDrawer, setComponent, component, chatId } = useDrawer();

     console.log('RightDrawer component:', component, chatId);

    // Example of a submit handler (replace with actual API call)
    const handleSubmit = (event) => {
        event.preventDefault();
        const formData = {
            hospital,
            address,
            contactPerson,
            phoneNumber,
            severity,
            additionalNote,
        };
        console.log('Admission Details Submitted:', formData);
        // You'd typically send this data to your backend here
        onClose(); // Close the drawer after submission
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Drawer
                anchor="right" // Slide from the right
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 450, md: 500 }, // Responsive width for the drawer
                        boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.1)', // Subtle shadow
                        borderRadius: '0px', // No border radius for the drawer itself
                        overflowX: 'hidden', // Prevent horizontal scroll
                        height: '100vh', // Full height of the viewport
                        zIndex: theme.zIndex.drawer + 5
                    },
                }}
                ModalProps={{
                    sx: {
                        zIndex: 1301, // Applies to the modal container including the backdrop
                    }
                }}
            >
                {
                    component === 'admission' ?
                        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            {/* Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <IconButton onClick={onClose} sx={{ mr: 2 }}>
                                    <ArrowBackIcon />
                                </IconButton>
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                    Admission Details
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 3 }} />

                            {/* Form Content */}
                            <Box component="form" onSubmit={handleSubmit} sx={{ flexGrow: 1, overflowY: 'auto', pr: 1, mr: -1, mt: 2 }}> {/* Added overflowY for scroll */}
                                <Stack spacing={2}>
                                    <TextField
                                        label="Hospital"
                                        variant="outlined"
                                        fullWidth
                                        value={hospital}
                                        onChange={(e) => setHospital(e.target.value)}
                                    />
                                    <TextField
                                        label="Address"
                                        variant="outlined"
                                        fullWidth
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                    />
                                    <Button
                                        variant="text" // Or 'outlined' if you want a border
                                        sx={{
                                            alignSelf: 'flex-start',
                                            textTransform: 'none',
                                            color: theme.palette.primary.main,
                                            fontWeight: 600,
                                            '& .MuiButton-startIcon': { mr: 0.5 },
                                            fontSize: '0.875rem' // Match the screenshot's font size
                                        }}
                                        startIcon={<ArrowForwardIcon />} // Matches the arrow icon in the screenshot
                                    >
                                        Use Google Map
                                    </Button>

                                    <TextField
                                        label="Contact Person"
                                        variant="outlined"
                                        fullWidth
                                        value={contactPerson}
                                        onChange={(e) => setContactPerson(e.target.value)}
                                    />
                                    <TextField
                                        label="Phone Number"
                                        variant="outlined"
                                        fullWidth
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        placeholder="Enter phone number" // Add a placeholder
                                    />

                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel id="severity-label">Level of severity</InputLabel>
                                        <Select
                                            labelId="severity-label"
                                            id="severity-select"
                                            value={severity}
                                            label="Level of severity"
                                            onChange={(e) => setSeverity(e.target.value)}
                                        >
                                            <MenuItem value="High (Emergency Admission)">High (Emergency Admission)</MenuItem>
                                            <MenuItem value="Medium">Medium</MenuItem>
                                            <MenuItem value="Low">Low</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        label="Referral Admission/Additional Note"
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={6} // Adjust rows to fit the content box size in the screenshot
                                        value={additionalNote}
                                        onChange={(e) => setAdditionalNote(e.target.value)}
                                        sx={{
                                            backgroundColor: theme.palette.primary.main + '20', // Light green background
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: 'transparent' }, // No border
                                                '&:hover fieldset': { borderColor: 'transparent' },
                                                '&.Mui-focused fieldset': { borderColor: 'transparent' },
                                            },
                                            '& .MuiInputLabel-outlined': { // Label in primary color
                                                color: theme.palette.primary.main,
                                                fontWeight: 600,
                                            },
                                            '& .Mui-focused .MuiInputLabel-outlined': {
                                                color: theme.palette.primary.main, // Keep primary color when focused
                                            },
                                        }}
                                    />
                                </Stack>
                            </Box>

                            {/* Footer (for submission) - often fixed at bottom */}
                            <Box sx={{ pt: 3, pb: 1 }}> {/* Padding for space before the button */}
                                <Button variant="contained" fullWidth size="large" type="submit">
                                    Save Details
                                </Button>
                            </Box>
                        </Box>
                        : (component === 'chat') ?

                            <Chat id={chatId}/> :

                            (component === 'labWork') ?  <LabWorkRequest /> :

                            (component === 'pharmacy') ? <ViewPrescription />:

                            (component === 'followup') ? <FollowUpAppointment /> :

                            ""
                }
            </Drawer>
        </ThemeProvider>
    );
}

export default RightDrawer;